import React, { useState } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import {
  MdEmail,
  MdLock,
  MdVisibility,
  MdVisibilityOff,
  MdNumbers,
} from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const ResetPassword = () => {
  const MailIcon = MdEmail as React.FC<React.SVGProps<SVGSVGElement>>;
  const LockIcon = MdLock as React.FC<React.SVGProps<SVGSVGElement>>;
  const EyeIcon = MdVisibility as React.FC<React.SVGProps<SVGSVGElement>>;
  const EyeOffIcon = MdVisibilityOff as React.FC<React.SVGProps<SVGSVGElement>>;
  const OtpIcon = MdNumbers as React.FC<React.SVGProps<SVGSVGElement>>;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    password: "",
    password_confirmation: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      alert("Passwords do not match");
      return;
    }

    setIsLoading(true);
    const success = await resetPassword(
      formData.email,
      formData.otp,
      formData.password,
      formData.password_confirmation
    );
    setIsLoading(false);

    if (success) {
      navigate("/login");
    }
  };

  return (
    <div className={GlobalStyles.containerCenter}>
      <div className={GlobalStyles.formContainer}>
        <div className={GlobalStyles.formHeader}>
          <h2 className={GlobalStyles.formHeaderTitle}>Reset Password</h2>
          <p className={GlobalStyles.formHeaderSubtitle}>
            Enter your email, the OTP sent to you, and your new password below.
          </p>
        </div>

        <form className="p-5" onSubmit={handleSubmit}>
          {/* Email */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="email" className={GlobalStyles.label}>
              Email Address
            </label>
            <div className={GlobalStyles.inputWrapper}>
              <MailIcon className={GlobalStyles.inputIcon} />
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                className={GlobalStyles.input}
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* OTP */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="otp" className={GlobalStyles.label}>
              OTP Code
            </label>
            <div className={GlobalStyles.inputWrapper}>
              <OtpIcon className={GlobalStyles.inputIcon} />
              <input
                id="otp"
                type="text"
                placeholder="Enter OTP"
                className={GlobalStyles.input}
                value={formData.otp}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Password */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="password" className={GlobalStyles.label}>
              New Password
            </label>
            <div className={`${GlobalStyles.inputWrapper} relative`}>
              <LockIcon className={GlobalStyles.inputIcon} />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                className={GlobalStyles.input}
                value={formData.password}
                onChange={handleChange}
                required
              />
              <div
                className="absolute right-3 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOffIcon className={GlobalStyles.inputIcon} />
                ) : (
                  <EyeIcon className={GlobalStyles.inputIcon} />
                )}
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          <div className={GlobalStyles.formGroup}>
            <label
              htmlFor="password_confirmation"
              className={GlobalStyles.label}
            >
              Confirm Password
            </label>
            <div className={`${GlobalStyles.inputWrapper} relative`}>
              <LockIcon className={GlobalStyles.inputIcon} />
              <input
                id="password_confirmation"
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm password"
                className={GlobalStyles.input}
                value={formData.password_confirmation}
                onChange={handleChange}
                required
              />
              <div
                className="absolute right-3 cursor-pointer"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? (
                  <EyeOffIcon className={GlobalStyles.inputIcon} />
                ) : (
                  <EyeIcon className={GlobalStyles.inputIcon} />
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className={`${GlobalStyles.buttonPrimary} !w-full mt-5 ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Resetting Password..." : "Reset Password"}
          </button>

          <div
            className={`mt-6 text-center ${CommonClasses.textXs} ${CommonClasses.textGray}`}
          >
            Back to{" "}
            <Link to="/login" className={GlobalStyles.linkAccent}>
              Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
